/**
 *
 * @author Matthew Rawson @ 11/09/2018 15:49
 * @description
 * @notes
 *
 *
 */
import React, {PureComponent, Fragment} from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import scrollIntoView from "scroll-into-view-if-needed"
import {get} from "lodash"

import id from "@irati/utils/id"
import {FormattedMessage, injectIntl} from "@irati/utils/lang"
import Expand from "@irati/ui/src/Expander"
import multiLineEllipsis from "@irati/ui/src/global/css/multiLineEllipsis"
import {RouterLink} from "@irati/ui/src/Link"
import Icon from "@irati/ui/src/Icon"
import {greys} from "@irati/ui/src/global/colors"
import HasPermissionTo from "@irati/utils/permissions/HasPermissionTo"

import {Para} from "../../../ui/Labels"
import FlexWrapper from "../../../ui/Layout/FlexWrapper"


const MINIMISED_LINES_TO_SHOW = 3

const ExpandedPara = styled(Para)`white-space: normal;`
const MinimisedPara = styled(Para)`
  ${multiLineEllipsis(MINIMISED_LINES_TO_SHOW, 18)}
  white-space: normal;
`

const IconWrapper = styled.div`
  margin-right: 20px;
  vertical-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledLink = styled(RouterLink)`
  color: ${greys.dark};
  :visited {
    color: ${greys.dark};
  }
`

const StyledLinkNoDecoration = styled(StyledLink)`
  text-decoration: none;
`


class CommentCellRenderer extends PureComponent {
  static propTypes = {
    formatMessage: PropTypes.func.isRequired,
    row: PropTypes.object.isRequired
  }


  constructor(props) {
    super(props)
    this.state = {
      isExpanded: false,
      expandComponent: null,
      paraComponent: null,
      needsExpand: false,
      text: get(props, "row.text")
    }

    const {row} = props
    this.idNamespace = id(`CommentCellRenderer_${row._index}`, props)
  }

  /** aggressive but autonomous way of always closing expanded cell contents */
  static getDerivedStateFromProps({row}, {text, expandComponent, paraComponent}) {
    if (row.text === text) return null

    if (expandComponent) expandComponent.close()
    return {
      isExpanded: false,
      text: row.text,
      needsExpand: CommentCellRenderer.determineExpandBasedOnTextHeight(paraComponent)
    }
  }

  static determineExpandBasedOnTextHeight = (paraComponent) => {
    return paraComponent && paraComponent.scrollHeight > paraComponent.clientHeight
  }

  doesItNeedExpand = () => {
    const {paraComponent, isExpanded} = this.state
    if (!paraComponent) return false
    if (isExpanded) return true
    return CommentCellRenderer.determineExpandBasedOnTextHeight(paraComponent)
  }

  updateDimensions = () => {
    const {needsExpand: needsExpandInState} = this.state
    const needsExpand = this.doesItNeedExpand()
    if (needsExpandInState !== needsExpand) this.setState({needsExpand})
  }

  componentDidMount() {
    window.addEventListener("resize", this.updateDimensions)
    this.updateDimensions()
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateDimensions)
  }


  toggleExpansion = () => {
    const {isExpanded} = this.state
    this.setState({isExpanded: !isExpanded}, () => {
      /** auto scroll back to the cell during minimisation  */
      if (isExpanded) scrollIntoView(document.getElementById(this.idNamespace), {scrollMode: "if-needed"})
    })
  }

  render() {
    const {formatMessage, original} = this.props
    const {isExpanded, needsExpand, text} = this.state
    const ParaComponent = isExpanded ? ExpandedPara : MinimisedPara
    const commentId = get(original, "id")
    const messageCount = get(original, "messages.total")
    const roomId = get(original, "messages.results[0].roomUuid")
    const roomLink = `/chat/${roomId || `?commentId=${commentId}`}`

    const linkProps = {styled: true, uppercase: false, inBody: true, to: roomLink}
    return (
      <Fragment>
        <ParaComponent
          medium
          flush
          id={this.idNamespace}
          ref={(paraComponent) => this.setState({paraComponent}, this.updateDimensions)}>{text}</ParaComponent>
        {needsExpand && (
          <div>
            <Expand
              ref={(expandComponent) => this.setState({expandComponent})}
              onChange={this.toggleExpansion}
              showText={formatMessage({id: "insights.analysis.comments.expander.seeMore"})}
              hideText={formatMessage({id: "insights.analysis.comments.expander.seeLess"})} />
          </div>
        )}
        <HasPermissionTo permissions={["insights.chatroom"]} Error={null}>
          <FlexWrapper>
            <StyledLinkNoDecoration {...linkProps}>
              {messageCount > 0 && <IconWrapper> <Icon icon="comment" /> {messageCount} </IconWrapper>}
            </StyledLinkNoDecoration>
            <StyledLink {...linkProps}>
              <FormattedMessage id="insights.anonChat.analysis.reply" />
            </StyledLink>
          </FlexWrapper>
        </HasPermissionTo>

      </Fragment>
    )
  }
}

export default injectIntl(CommentCellRenderer)
